import React, { useRef, useState, useEffect } from "react";
import { motion, useMotionValue } from "framer-motion";
import { Trash2, RotateCw, Pencil, Play, Music2 } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { sounds } from "@/lib/sounds";

export default function Polaroid({ pol, boardRef, onOpen, onDelete, onPositionChange, onBringFront, onCaption }) {
  const x = useMotionValue(pol.x);
  const y = useMotionValue(pol.y);
  const [rot, setRot] = useState(pol.rotation);
  const [scale, setScale] = useState(pol.scale || 1);
  const [tearing, setTearing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cap, setCap] = useState(pol.caption || "");
  const [confirmDel, setConfirmDel] = useState(false);
  const moved = useRef(false);
  const delTimer = useRef(null);
  useEffect(() => () => clearTimeout(delTimer.current), []);

  const doDelete = () => {
    setTearing(true);
    sounds.crumple();
    setTimeout(() => onDelete(pol.id), 460);
  };

  const rotate = () => { const n = rot + 15; setRot(n); sounds.tack(); onPositionChange(pol.id, { rotation: n }); };
  const resize = () => { const n = scale >= 1.4 ? 0.8 : scale + 0.2; setScale(n); onPositionChange(pol.id, { scale: n }); };
  const saveCap = () => { setEditing(false); onCaption(pol.id, cap); };
  const isVideo = (pol.content_type || "").startsWith("video/");
  const isAudio = (pol.content_type || "").startsWith("audio/");

  return (
    <motion.div
      data-testid={`polaroid-${pol.id}`}
      className="absolute bg-white p-2.5 pb-9 paper-shadow cursor-grab active:cursor-grabbing select-none group"
      style={{ x, y, rotate: rot, scale, zIndex: pol.z_index, width: 176, touchAction: "none" }}
      drag
      dragConstraints={boardRef}
      dragMomentum={false}
      whileDrag={{ scale: scale * 1.05, zIndex: 9999, boxShadow: "0 20px 38px rgba(0,0,0,.35)" }}
      animate={tearing ? { scale: 0.1, rotate: rot + 40, opacity: 0 } : {}}
      transition={{ duration: 0.44 }}
      onDragStart={() => { moved.current = false; onBringFront(pol.id); }}
      onPointerDown={() => onBringFront(pol.id)}
      onDrag={(e, info) => { if (Math.abs(info.offset.x) > 6 || Math.abs(info.offset.y) > 6) moved.current = true; }}
      onDragEnd={() => onPositionChange(pol.id, { x: x.get(), y: y.get() })}
    >
      <span className="washi" style={{ opacity: 0.6 }} />
      <div className="w-full aspect-square bg-[#efeae2] overflow-hidden relative" onClick={() => { if (!moved.current) onOpen(pol); }}>
        {isVideo ? (
          <>
            <video src={`${fileUrl(pol.storage_path)}#t=0.1`} className="w-full h-full object-cover pointer-events-none" muted playsInline preload="metadata" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/15">
              <span className="w-10 h-10 rounded-full bg-white/85 flex items-center justify-center paper-shadow">
                <Play className="w-5 h-5 text-[#2b2620] ml-0.5" fill="#2b2620" />
              </span>
            </div>
          </>
        ) : isAudio ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#efeae2]">
            <span className="w-12 h-12 rounded-full bg-white/85 flex items-center justify-center paper-shadow">
              <Music2 className="w-6 h-6 text-[#a0703c]" />
            </span>
          </div>
        ) : (
          <img src={fileUrl(pol.storage_path)} alt={pol.caption || "polaroid"} className="w-full h-full object-cover" draggable={false} />
        )}
      </div>
      <div className="absolute bottom-1.5 left-0 right-0 px-2 text-center">
        {editing ? (
          <input
            data-testid={`polaroid-caption-input-${pol.id}`}
            autoFocus value={cap} onChange={(e) => setCap(e.target.value)} onBlur={saveCap}
            onKeyDown={(e) => e.key === "Enter" && saveCap()}
            className="w-full bg-transparent text-center font-hand text-lg text-[#3a2f22] outline-none border-b border-dashed border-[#c9b48f]"
            placeholder="caption…"
          />
        ) : (
          <p className="font-hand text-lg text-[#3a2f22] truncate cursor-text" onClick={(e) => { e.stopPropagation(); setEditing(true); }}>
            {cap || <span className="text-[#b7a98f]">add a caption…</span>}
          </p>
        )}
      </div>

      <div className="absolute -top-3 -right-3 flex-col gap-1 flex opacity-80 group-hover:opacity-100 transition-opacity">
        <button data-testid={`polaroid-rotate-${pol.id}`} onClick={(e) => { e.stopPropagation(); rotate(); }} className="w-8 h-8 rounded-full bg-white paper-shadow flex items-center justify-center text-[#5a5148] hover:text-[#007aff]"><RotateCw className="w-4 h-4" /></button>
        <button data-testid={`polaroid-resize-${pol.id}`} onClick={(e) => { e.stopPropagation(); resize(); }} className="w-8 h-8 rounded-full bg-white paper-shadow flex items-center justify-center text-[#5a5148] hover:text-[#007aff] font-display font-black text-sm">±</button>
        <button data-testid={`polaroid-caption-${pol.id}`} onClick={(e) => { e.stopPropagation(); setEditing(true); }} className="w-8 h-8 rounded-full bg-white paper-shadow flex items-center justify-center text-[#5a5148] hover:text-[#007aff]"><Pencil className="w-4 h-4" /></button>
        <button
          data-testid={`polaroid-delete-${pol.id}`}
          onClick={(e) => { e.stopPropagation(); if (confirmDel) { doDelete(); } else { setConfirmDel(true); delTimer.current = setTimeout(() => setConfirmDel(false), 2500); } }}
          className={`w-8 h-8 rounded-full paper-shadow flex items-center justify-center ${confirmDel ? "bg-[#ff3b30] text-white animate-pulse" : "bg-white text-[#5a5148] hover:text-[#ff3b30]"}`}
          title={confirmDel ? "Tap again to delete" : "Delete"}
        ><Trash2 className="w-4 h-4" /></button>
      </div>
    </motion.div>
  );
}
